import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MobileNav } from '../components/MobileNav';
import { Coins, ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react';
import { API } from '../config';

const playfairFont = "'Playfair Display', serif";

const typeLabel = {
  topup_approved: 'Top Up Approved',
  lucky_draw: 'Lucky Draw',
  refund: 'Refund',
  admin_adjustment: 'Admin Adjustment'
};

export const WalletHistory = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [topups, setTopups] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [txnsRes, balRes, topupsRes] = await Promise.all([
          axios.get(`${API}/wallet/transactions`, { withCredentials: true }),
          axios.get(`${API}/wallet/balance`, { withCredentials: true }),
          axios.get(`${API}/wallet/topups`, { withCredentials: true }),
        ]);
        setTransactions(txnsRes.data);
        setBalance(balRes.data.coin_balance || 0);
        setTopups(topupsRes.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatCoins = (n) => new Intl.NumberFormat('id-ID').format(Math.abs(n));
  const formatIDR = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);
  const formatDate = (d) => new Date(d).toLocaleString('id-ID');

  return (
    <div className="min-h-screen pb-24" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.9)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="hover:opacity-80" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
          </button>
          <h1 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Coin History</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="rounded-2xl p-6 backdrop-blur-xl" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(3,32,20,0.4))', border: '1px solid rgba(212,175,55,0.3)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Coins className="w-5 h-5" style={{ color: '#D4AF37' }} />
            <span className="text-sm" style={{ color: '#A3B8AC' }}>Current Balance</span>
          </div>
          <p className="text-4xl font-light" style={{ fontFamily: playfairFont, color: '#D4AF37' }}>
            {new Intl.NumberFormat('id-ID').format(balance)} <span className="text-base" style={{ color: '#A3B8AC' }}>Coin</span>
          </p>
          <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>≈ Rp {new Intl.NumberFormat('id-ID').format(balance * 1000)} · 1 Coin = Rp 1.000</p>
        </div>

        {/* ===== Top Up History (with total_transfer + coin_received) ===== */}
        {topups.length > 0 && (
          <div data-testid="topup-history-section">
            <h2 className="text-xl font-light mb-3" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Riwayat Top Up</h2>
            <div className="space-y-2">
              {topups.map((tu) => {
                const total = tu.total_transfer ?? tu.amount;
                const statusColor = tu.status === 'approved' ? '#22C55E' : (tu.status === 'rejected' ? '#EF4444' : '#F59E0B');
                return (
                  <div key={tu.id} className="rounded-xl p-4 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid={`topup-row-${tu.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-xs uppercase tracking-wider" style={{ color: statusColor }}>{tu.status?.replace('_', ' ') || 'pending'}</p>
                        <p className="text-xs" style={{ color: '#A3B8AC' }}>{formatDate(tu.created_at)} · {tu.payment_method?.toUpperCase()}</p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37' }}>+{formatCoins(tu.coins)} Coin</span>
                    </div>
                    <div className="grid grid-cols-2 gap-y-1 text-xs">
                      <span style={{ color: '#A3B8AC' }}>Total Transfer Dibayar:</span>
                      <span className="text-right font-medium" style={{ color: '#F8FAF9' }} data-testid={`topup-total-${tu.id}`}>{formatIDR(total)}</span>
                      <span style={{ color: '#A3B8AC' }}>Coin Diterima:</span>
                      <span className="text-right font-medium" style={{ color: '#22C55E' }} data-testid={`topup-coins-${tu.id}`}>{formatCoins(tu.coins)} Coin</span>
                      {tu.unique_code != null && tu.unique_code > 0 && (
                        <>
                          <span style={{ color: '#A3B8AC' }}>Kode Unik:</span>
                          <span className="text-right" style={{ color: '#F59E0B' }}>Rp {tu.unique_code}</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12" style={{ color: '#A3B8AC' }}>Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Coins className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
            <p style={{ color: '#A3B8AC' }}>No transactions yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((t) => {
              const isPositive = t.amount > 0;
              const Icon = isPositive ? TrendingUp : TrendingDown;
              const color = isPositive ? '#22C55E' : '#EF4444';
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl p-4 backdrop-blur-xl flex items-center gap-4"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid={`txn-${t.id}`}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${color}20`, border: `1px solid ${color}50` }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: '#F8FAF9' }}>{typeLabel[t.type] || t.type}</p>
                    <p className="text-xs" style={{ color: '#A3B8AC' }}>{t.description}</p>
                    <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>{formatDate(t.created_at)}</p>
                  </div>
                  <p className="text-lg font-semibold" style={{ color, fontFamily: playfairFont }}>
                    {isPositive ? '+' : '-'}{formatCoins(t.amount)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
};
